import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';
import { useLayout } from '../../../contexts/LayoutContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import { useToast } from '../../../components/Toast';
import AccessDenied from '../../../components/AccessDenied';
import ErrorDisplay from '../../../components/ErrorDisplay';
import DataTable from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';
import type { Column, Filter, HeaderActions } from '../../../components/DataTable';
import ToolbarActions from '../../../components/ToolbarActions';
import { formatNumber } from '../../../utils/formatters';
import EmailWithStatus from '../../../components/EmailWithStatus';
import { PERMISSIONS, STATUS_CONFIG, WORKFLOW_COLUMNS } from './ServiceLogsConfig';
// BoardView stays eager — it's the daily working surface, must feel instant.
import BoardView from './ServiceLogsBoardView';
import { loadSendHistory, type InvoiceSends } from './serviceLogsSendHistory';

// Lazy-loaded modals + heavy children. Invisible on first paint, so they
// don't need to ship in the initial bundle. Prefetched on mount below so
// the chunks are warm by the time the operator clicks anything.
const SendInvoiceModal = lazy(() => import('../../../components/SendInvoiceModal'));
const ApproveServiceLogModal = lazy(() => import('../../../components/ApproveServiceLogModal'));
const WhatsAppModal = lazy(() => import('../../../components/WhatsAppModal'));
const InvoiceManager = lazy(() => import('./InvoiceManager'));
const InvoiceDetailModal = lazy(() => import('./InvoiceDetailModal'));
const ViewServiceLogModal = lazy(() => import('./ServiceLogsViewModal'));

// ── Types ──────────────────────────────────────────────────────

/** Row from the unified_service_reports view */
interface ServiceLogRow {
  id: string;
  source?: string;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string;
  service_date: string;
  total_amount?: number;
  vat_inclusive?: boolean;
  territory_name?: string;
  operator_name?: string;
  invoice_number?: string;
  invoice_id?: string;
  status: string;
  payment_method?: string;
  internal_notes?: string;
  booking_code?: string;
  additional_cc_emails?: string;
  [key: string]: unknown;
}

/** Shape of invoice data loaded for the send modal */
interface SendInvoiceData {
  id: string;
  invoice_number?: string;
  customer_name?: string;
  customer_email?: string;
  customer_company?: string;
  total?: number;
  status?: string;
  territory_id?: string;
  referral_discount_applied?: string | number;
  referral_credit_applied?: string | number;
  additional_cc_emails?: string;
  [key: string]: unknown;
}

// ── Component ──────────────────────────────────────────────────

type PermissionLevel = Parameters<ReturnType<typeof useAuth>['hasPermission']>[1];

function ServiceLogs() {
  const navigate = useNavigate();
  const { hasPermission, appMetadata } = useAuth();
  const { showToast } = useToast();
  // taxRate sourced from country_config.vat_rate via useConfig — no destructure
  // default so a missing-config scenario surfaces visibly (NaN math) rather than
  // silently applying ZA's 15% to every country.
  const { formatCurrency, taxRate, countryConfig } = useConfig();
  const { isMobile } = useLayout();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('service_logs');

  // Permission checks
  const canCreate = hasPermission('service_logs', PERMISSIONS.CREATE as PermissionLevel);
  const canEdit = hasPermission('service_logs', PERMISSIONS.EDIT as PermissionLevel);
  const canApprove = hasPermission('service_logs', PERMISSIONS.APPROVE as PermissionLevel);
  const canSend = hasPermission('invoices', 'send' as PermissionLevel);
  const canWhatsApp = hasPermission('invoices', 'whatsapp' as PermissionLevel);
  const canUnlinkDraft = hasPermission('invoices', 'unlink_draft' as PermissionLevel);

  // State
  const [serviceLogs, setServiceLogs] = useState<ServiceLogRow[]>([]);
  const [bouncingByCustomerId, setBouncingByCustomerId] = useState<Map<string, { lastBounceAt: string | null }>>(new Map());
  const [bouncingEmails, setBouncingEmails] = useState<Set<string>>(new Set());
  // Email and WhatsApp sends per invoice number, for the Sent and Overdue cards.
  // Only people who can send invoices (head office) see them.
  const [sendHistory, setSendHistory] = useState<Map<string, InvoiceSends>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingLog, setViewingLog] = useState<ServiceLogRow | null>(null);
  const [viewingInvoiceId, setViewingInvoiceId] = useState<string | null>(null);
  const [creatingInvoiceFor, setCreatingInvoiceFor] = useState<string | null>(null);
  const [operationMessage, setOperationMessage] = useState<string | null>(null);

  // Send Invoice Modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState<SendInvoiceData | null>(null);

  // Approve Service Log Modal state — replaces the old confirm() pop-up
  // with a richer "approve & set invoice options" step.
  const [approvingLog, setApprovingLog] = useState<ServiceLogRow | null>(null);

  // Invoice Manager state
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);

  // WhatsApp Modal state
  const [whatsAppLog, setWhatsAppLog] = useState<ServiceLogRow | null>(null);

  // Follow-up modal state
  const [followUpLog, setFollowUpLog] = useState<ServiceLogRow | null>(null);

  // View mode: 'list' or 'card' - saved to localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('serviceLogsViewMode') || 'list';
  });

  // Mobile view mode: 'list' or 'board' - saved to localStorage
  const [mobileViewMode, setMobileViewMode] = useState(() => {
    return localStorage.getItem('serviceLogsMobileViewMode') || 'list';
  });

  useEffect(() => {
    localStorage.setItem('serviceLogsMobileViewMode', mobileViewMode);
  }, [mobileViewMode]);

  // Card view search and filter state
  const [cardSearch, setCardSearch] = useState('');
  const [cardTerritoryFilter, setCardTerritoryFilter] = useState('');
  const [cardStatusFilter, setCardStatusFilter] = useState('');

  // Mobile: collapsible filter panel (matches the DataTable mobile pattern).
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);

  // Per-column sort state - persisted to localStorage
  const [columnSorts, setColumnSorts] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('serviceLogsColumnSorts');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem('serviceLogsColumnSorts', JSON.stringify(columnSorts));
  }, [columnSorts]);

  const setColumnSort = (colId: string, sortType: string) => {
    setColumnSorts(prev => ({ ...prev, [colId]: sortType }));
  };

  useEffect(() => {
    localStorage.setItem('serviceLogsViewMode', viewMode);
  }, [viewMode]);

  // Scope: by default only fetch active statuses + the most recent 50 paid.
  // 567 paid rows lived in the unified view on ZA today and the operator
  // workflow only cares about the live ones, so fetching the whole history
  // every visit was wasted bytes and parse time. But an operator only has a
  // few hundred paid logs at most, and capping them at the newest 50 hid their
  // older paid history. So operators (own/territory scope) load all their paid
  // logs up front; wide-scope admins (global/all), who can have thousands,
  // keep the recent-50 default and opt in via the "Show all paid" toggle.
  const wideScopeMeta = appMetadata as { scope?: string; is_super_admin?: boolean } | null;
  const isWideScope = wideScopeMeta?.is_super_admin === true
    || wideScopeMeta?.scope === 'global'
    || wideScopeMeta?.scope === 'all';
  const [showAllPaid, setShowAllPaid] = useState(!isWideScope);
  /** Everything there is, not everything loaded. Drives the table's own notice. */
  const [totalCount, setTotalCount] = useState(0);

  // Load data on mount
  useEffect(() => {
    loadData(showAllPaid);
  }, [showAllPaid]);

  // Send history follows the loaded cards, and reloads on its own after a WhatsApp send (which
  // changes no status, so the board itself need not reload). Best effort: if it fails the
  // board still works and the icons stay grey. A newer load always wins over an older one.
  const [sendHistoryTick, setSendHistoryTick] = useState(0);
  useEffect(() => {
    if (!canSend) return;
    let stale = false;
    loadSendHistory(supabase, serviceLogs)
      .then(history => { if (!stale) setSendHistory(history); })
      .catch(err => console.error('Error loading send history:', err));
    return () => { stale = true; };
  }, [serviceLogs, canSend, sendHistoryTick]);

  // Warm the lazy modal chunks in the background so clicks feel instant.
  // Best-effort; failure just defers the chunk fetch to the click moment.
  useEffect(() => {
    import('../../../components/SendInvoiceModal').catch(() => {});
    import('../../../components/ApproveServiceLogModal').catch(() => {});
    import('../../../components/WhatsAppModal').catch(() => {});
    import('./InvoiceManager').catch(() => {});
    import('./InvoiceDetailModal').catch(() => {});
    import('./ServiceLogsViewModal').catch(() => {});
  }, []);

  const loadData = async (loadAllPaid: boolean) => {
    try {
      setLoading(true);
      setError(null);

      // Two parallel queries: all non-paid + recent paid (50 by default, all if toggled).
      // Faster than a single OR query and easier to reason about.
      const activeP = supabase
        .from('unified_service_reports')
        .select('*')
        .neq('status', 'paid')
        .order('service_date', { ascending: false });

      const paidP = loadAllPaid
        ? supabase
            .from('unified_service_reports')
            .select('*')
            .eq('status', 'paid')
            .order('service_date', { ascending: false })
        : supabase
            .from('unified_service_reports')
            .select('*')
            .eq('status', 'paid')
            .order('service_date', { ascending: false })
            .limit(50);

      const [{ data: active, error: aErr }, { data: paid, error: pErr }] = await Promise.all([activeP, paidP]);

      if (aErr) throw aErr;
      if (pErr) throw pErr;

      const rows = ([...(active ?? []), ...(paid ?? [])] as ServiceLogRow[]);
      setServiceLogs(rows);

      // How many there really are, so the table can say "newest 76 of 6,218"
      // instead of "76 items", which reads as all of them. head:true fetches
      // the count only, no rows.
      if (!loadAllPaid) {
        const { count } = await supabase
          .from('unified_service_reports')
          .select('*', { count: 'exact', head: true });
        setTotalCount(count ?? rows.length);
      } else {
        setTotalCount(rows.length);
      }

      const { data: bouncing } = await supabase
        .from('customers')
        .select('id, email, email_last_bounce_at')
        .eq('email_bouncing', true);
      const map = new Map<string, { lastBounceAt: string | null }>();
      const emails = new Set<string>();
      (bouncing as Array<{ id: string; email: string | null; email_last_bounce_at: string | null }> | null)?.forEach(c => {
        map.set(c.id, { lastBounceAt: c.email_last_bounce_at });
        if (c.email) emails.add(c.email.toLowerCase());
      });
      setBouncingByCustomerId(map);
      setBouncingEmails(emails);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Extract unique territory names for filter dropdown
  const territoryOptions = useMemo(() => {
    const territories = new Map<string, string>();
    serviceLogs.forEach(log => {
      const name = log.territory_name;
      if (name) {
        territories.set(name, name);
      }
    });
    return Array.from(territories.values()).sort().map(name => ({
      value: name,
      label: name
    }));
  }, [serviceLogs]);

  // Status filter options
  const statusOptions = useMemo(() => {
    const granularOptions = Object.entries(STATUS_CONFIG).map(([key, config]) => ({
      value: key,
      label: config.label
    }));
    return [
      { value: 'unpaid', label: 'Unpaid', matchValues: ['sent', 'overdue'] },
      ...granularOptions
    ];
  }, []);

  // Sort function factory
  const createSortFn = (sortType: string) => (a: ServiceLogRow, b: ServiceLogRow) => {
    switch (sortType) {
      case 'date_asc':
        return new Date(a.service_date).getTime() - new Date(b.service_date).getTime();
      case 'customer_asc':
        return (a.customer_name || '').localeCompare(b.customer_name || '');
      case 'customer_desc':
        return (b.customer_name || '').localeCompare(a.customer_name || '');
      case 'amount_desc':
        return (b.total_amount || 0) - (a.total_amount || 0);
      case 'amount_asc':
        return (a.total_amount || 0) - (b.total_amount || 0);
      case 'invoice_asc':
      case 'invoice_desc': {
        const aInv = a.invoice_number || '';
        const bInv = b.invoice_number || '';
        if (!aInv && !bInv) return 0;
        if (!aInv) return 1;
        if (!bInv) return -1;
        const cmp = aInv.localeCompare(bInv, undefined, { numeric: true, sensitivity: 'base' });
        return sortType === 'invoice_asc' ? cmp : -cmp;
      }
      case 'date_desc':
      default:
        return new Date(b.service_date).getTime() - new Date(a.service_date).getTime();
    }
  };

  // Group logs by status for Kanban board
  const logsByStatus = useMemo(() => {
    let filtered = serviceLogs;

    if (cardSearch) {
      const search = cardSearch.toLowerCase();
      filtered = filtered.filter(log =>
        log.customer_name?.toLowerCase().includes(search) ||
        log.customer_email?.toLowerCase().includes(search) ||
        log.invoice_number?.toLowerCase().includes(search) ||
        log.territory_name?.toLowerCase().includes(search) ||
        log.operator_name?.toLowerCase().includes(search) ||
        log.territory_name?.toLowerCase().includes(search) ||
        log.booking_code?.toLowerCase().includes(search)
      );
    }

    if (cardTerritoryFilter) {
      filtered = filtered.filter(log => (log.territory_name || log.territory_name) === cardTerritoryFilter);
    }

    const grouped: Record<string, ServiceLogRow[]> = {};
    WORKFLOW_COLUMNS.forEach(col => {
      grouped[col.id] = [];
    });
    filtered.forEach((log: ServiceLogRow) => {
      if (grouped[log.status]) {
        grouped[log.status]!.push(log);
      }
    });

    Object.keys(grouped).forEach(colId => {
      const sortType = columnSorts[colId] || 'date_desc';
      grouped[colId]!.sort(createSortFn(sortType));
    });

    return grouped;
  }, [serviceLogs, cardSearch, cardTerritoryFilter, columnSorts]);

  // Mobile List tab: same search + territory filter as the Board tab, applied
  // to the flat list. Mirrors the logsByStatus filter so both mobile tabs agree.
  const mobileFilteredLogs = useMemo(() => {
    let filtered = serviceLogs;

    if (cardSearch) {
      const search = cardSearch.toLowerCase();
      filtered = filtered.filter(log =>
        log.customer_name?.toLowerCase().includes(search) ||
        log.customer_email?.toLowerCase().includes(search) ||
        log.invoice_number?.toLowerCase().includes(search) ||
        log.territory_name?.toLowerCase().includes(search) ||
        log.operator_name?.toLowerCase().includes(search) ||
        log.booking_code?.toLowerCase().includes(search)
      );
    }

    if (cardTerritoryFilter) {
      filtered = filtered.filter(log => log.territory_name === cardTerritoryFilter);
    }

    if (cardStatusFilter) {
      // Mirror the desktop table filter: the "unpaid" option matches several
      // underlying statuses, the rest match the status one-to-one.
      const opt = statusOptions.find(o => o.value === cardStatusFilter) as { matchValues?: string[] } | undefined;
      const matches = opt?.matchValues ?? [cardStatusFilter];
      filtered = filtered.filter(log => matches.includes(log.status));
    }

    return filtered;
  }, [serviceLogs, cardSearch, cardTerritoryFilter, cardStatusFilter, statusOptions]);

  const handleNewServiceLog = () => {
    navigate('/service-logs/new?returnTo=service-logs');
  };

  const handleOpenViewModal = async (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    if (log.source === 'active' && log.id?.includes('-')) {
      try {
        const { data: fullLog, error } = await supabase
          .from('service_logs')
          .select(`
            *,
            items:service_log_items(*),
            split_code:split_codes(id, code, description),
            referral_code:referral_codes(id, code),
            customer:customers(is_pos)
          `)
          .eq('id', log.id)
          .single();

        if (!error && fullLog) {
          // A referral credit is held on the invoice, not the log, so the log alone cannot
          // say what the customer actually owes. Without this the two screens disagreed:
          // the invoice showed the credit and what was left to pay, the log showed neither
          // (INV-ZA-025165 / SL-ZA-001712, R250).
          let creditFields: Record<string, unknown> = {};
          const invoiceId = (fullLog as { invoice_id?: string }).invoice_id || log.invoice_id;
          if (invoiceId) {
            const { data: inv } = await supabase
              .from('invoices')
              .select('referral_credit_applied, balance_due')
              .eq('id', invoiceId)
              .maybeSingle();
            if (inv) {
              creditFields = {
                referral_credit_applied: inv.referral_credit_applied,
                invoice_balance_due: inv.balance_due,
              };
            }
          }

          // Flatten customer.is_pos onto the row so the view modal can render
          // the POS pill without needing the full customer record shape.
          const customerEmbed = (fullLog as { customer?: { is_pos?: boolean } | null }).customer;
          const flattened = {
            ...log,
            ...(fullLog as Record<string, unknown>),
            ...creditFields,
            customer_is_pos: customerEmbed?.is_pos === true,
          };
          setViewingLog(flattened as ServiceLogRow);
          return;
        }
      } catch (err) {
        console.error('Error loading full log:', err);
      }
    }

    setViewingLog(log);
  };

  const handleEditLog = (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (log.invoice_number) {
      setViewingLog(null);
      setEditingInvoiceNumber(log.invoice_number);
    } else {
      navigate(`/service-logs/${log.id}?returnTo=service-logs`);
    }
  };

  const handleCloseInvoiceManager = () => {
    setEditingInvoiceNumber(null);
    loadData(showAllPaid);
  };

  // "Approve service log" entry — opens the ApproveServiceLogModal. The
  // actual invoice creation happens in executeCreateInvoice once the
  // approver has confirmed the recipient, CCs, payment terms, and notes.
  const handleCreateInvoice = (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (creatingInvoiceFor) return;
    setApprovingLog(log);
  };

  const executeCreateInvoice = async (
    log: ServiceLogRow,
    overrides: {
      recipient_email: string | null;
      additional_cc_emails: string | null;
      notes: string | null;
      payment_terms_days: number | null;
    },
  ): Promise<void> => {
    setCreatingInvoiceFor(log.id);
    setOperationMessage('Creating invoice...');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=create_from_service_log`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ service_log_id: log.id, ...overrides }),
        },
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        const msg = result.error || 'Failed to create invoice';
        // The server blocks a second invoice on the same service log. That
        // usually means the first click already succeeded and the page hadn't
        // caught up, so this is not a failure: refresh so the invoice shows and
        // tell the user calmly, rather than a red error that invites another go.
        if (response.status === 409 || /already has invoice|already an invoice/i.test(msg)) {
          setOperationMessage(null);
          setApprovingLog(null);
          await loadData(showAllPaid);
          showToast(msg.replace(/\.?\s*Refresh the page to see it\.?/i, '') + '. The list has been refreshed.', 'info');
          return;
        }
        throw new Error(msg);
      }

      setOperationMessage(null);
      setApprovingLog(null);
      await loadData(showAllPaid);
    } catch (err) {
      console.error('Error creating invoice:', err);
      showToast('Error creating invoice: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
      // Re-throw so the modal can keep itself open (the button re-enables).
      throw err;
    } finally {
      setCreatingInvoiceFor(null);
      setOperationMessage(null);
    }
  };

  // additionalCcEmailsHint is a legacy carry-over from when callers tried
  // (and failed) to pre-fill CCs from the service log. The invoice's own
  // additional_cc_emails column is the source of truth now; we only fall
  // back to the hint if the invoice row didn't return one.
  const handleSendInvoice = async (invoiceId: string, additionalCcEmailsHint = '') => {
    if (!invoiceId) return;

    try {
      const { data: invoice, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, customer_name, customer_email, customer_company, total, status, territory_id, referral_discount_applied, referral_credit_applied, additional_cc_emails')
        .eq('id', invoiceId)
        .single();

      if (error) throw error;

      const inv = invoice as Record<string, unknown>;
      setSendingInvoice({
        ...invoice,
        additional_cc_emails: (inv.additional_cc_emails as string | null) || additionalCcEmailsHint || '',
      } as SendInvoiceData);
      setShowSendModal(true);
    } catch (err) {
      console.error('Error loading invoice:', err);
      showToast('Error loading invoice: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
    }
  };

  const handleSendSuccess = (message: string) => {
    loadData(showAllPaid);
    showToast(message || 'Invoice sent successfully!', 'success');
  };

  const handleUnlinkDraft = async (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    if (log.status !== 'draft') {
      showToast('Only draft invoices can be deleted this way.', 'warning');
      return;
    }

    const confirmed = confirm(
      `UNDO APPROVE\n\n` +
      `Invoice: ${log.invoice_number}\n` +
      `Customer: ${log.customer_name}\n` +
      `Amount: ${formatCurrency(log.total_amount || 0)}\n\n` +
      `This will:\n` +
      `- Remove the draft invoice and all related bills\n` +
      `- Reverse any applied referral credits\n` +
      `- Return the service log to PENDING for editing\n` +
      `- Delete from Zoho Books if synced\n\n` +
      `The service log data (items, customer, etc.) will be preserved.\n\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) return;

    setOperationMessage('Deleting draft invoice...');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=unlink&id=${log.invoice_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete draft invoice');
      }

      setOperationMessage(null);
      setViewingLog(null);

      showToast(result.message || 'Draft invoice deleted', 'success');
      await loadData(showAllPaid);
    } catch (err) {
      console.error('Error unlinking draft:', err);
      showToast('Error deleting draft invoice: ' + (err instanceof Error ? err.message : 'Unknown error'), 'error');
    } finally {
      setOperationMessage(null);
    }
  };

  const handleWhatsApp = (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setWhatsAppLog(log);
  };

  const handleFollowUp = (log: ServiceLogRow, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!log.invoice_id) return;
    setFollowUpLog(log);
  };

  const formatDateLocal = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Permission loading state
  if (permissionLoading) return null;

  if (!canAccess) {
    return <AccessDenied feature="service_logs" />;
  }

  // Loading state handled by DataTable component

  if (error) {
    return <ErrorDisplay error={error} onRetry={() => loadData(showAllPaid)} feature="service_logs" />;
  }

  function handleExportCSV() {
    const headers = ['Customer', 'Email', 'Date', 'Amount', 'VAT Inclusive', 'Territory', 'Operator', 'Invoice #', 'Status', 'Booking Code'];
    const rows = serviceLogs.map(sl => [
      sl.customer_name || '',
      sl.customer_email || '',
      sl.service_date || '',
      String(sl.total_amount ?? 0),
      sl.vat_inclusive ? 'Yes' : 'No',
      sl.territory_name || '',
      sl.operator_name || '',
      sl.invoice_number || '',
      sl.status || '',
      sl.booking_code || '',
    ]);

    const escapeCSV = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csv = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Filtered-view notice. Only wide-scope users (head office) get the capped
  // default, so only they need telling the list is partial. Operators load all
  // their own paid history up front, so they never see this.
  // The list view no longer needs this: the table says it itself, and searches
  // the rest by itself. Card view and mobile have their own search, so they
  // keep the notice, now stating the real numbers rather than "the 50 most
  // recent", which told you the rule instead of what you were looking at.
  const historyNotice = isWideScope && !showAllPaid && totalCount > serviceLogs.length ? (
    <div className="flex-between gap-sm text-sm text-muted mb-sm">
      <span>
        Showing the newest {formatNumber(serviceLogs.length)} of{' '}
        {formatNumber(totalCount)}. Older ones will not be found by a search yet.
      </span>
      <button type="button" className="btn-link" onClick={() => setShowAllPaid(true)}>
        Load all
      </button>
    </div>
  ) : null;

  // Columns for DataTable
  const columns: Column[] = [
    {
      key: 'customer_name',
      label: 'Customer',
      sortable: true,
      render: (row: Record<string, unknown>) => (
        <div>
          <div className="fw-500">
            {String(row.customer_name ?? '')}
            {!!row.internal_notes && <span title={String(row.internal_notes)} className="chip chip--warning ml-xs">Note</span>}
          </div>
          {!!row.customer_email && (
            <div className="sl-sub-text">
              <EmailWithStatus
                email={String(row.customer_email)}
                isBouncing={bouncingByCustomerId.has(String(row.customer_id ?? ''))}
                lastBounceAt={bouncingByCustomerId.get(String(row.customer_id ?? ''))?.lastBounceAt ?? null}
                compact
              />
            </div>
          )}
        </div>
      )
    },
    { key: 'service_date', label: 'Date', sortable: true, hideOnMobile: true, render: (row: Record<string, unknown>) => formatDateLocal(row.service_date as string) },
    {
      key: 'total_amount',
      label: 'Amount',
      sortable: true,
      align: 'right',
      render: (row: Record<string, unknown>) => (
        <div>
          <div>{formatCurrency(Number(row.total_amount) || 0)}{!!row.vat_inclusive && <span className="sl-vat-marker">inc</span>}</div>
          {!!row.invoice_number && (
            <div className="sl-invoice-num">{String(row.invoice_number)}</div>
          )}
        </div>
      )
    },
    {
      key: 'territory_name',
      label: 'Territory',
      hideOnMobile: true,
      render: (row: Record<string, unknown>) => row.territory_name ? <span className="code-chip">{String(row.territory_name)}</span> : '\u2014'
    },
    {
      key: 'operator_name',
      label: 'Operator',
      hideOnMobile: true,
      render: (row: Record<string, unknown>) => row.operator_name ? <span>{String(row.operator_name)}</span> : '\u2014'
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: Record<string, unknown>) => {
        const pillMap: Record<string, string> = {
          pending: 'pending', draft: 'draft', sent: 'sent',
          overdue: 'overdue', paid: 'paid', void: 'voided'
        };
        const labelMap: Record<string, string> = {
          pending: 'Pending', draft: 'Draft', sent: 'Sent',
          overdue: 'Overdue', paid: 'Paid', void: 'Void'
        };
        const s = row.status as string;
        const methodLabels: Record<string, string> = { paystack: 'PSTK', eft: 'EFT', cash: 'Cash', card: 'Card', other: 'Other' };
        return (
          <div className="sl-status-cell">
            <span className={`status-pill status-pill--${pillMap[s] || 'draft'}`}>
              <span className="status-pill-dot" style={{ background: 'currentColor' }} />
              {labelMap[s] || s}
            </span>
            {s === 'paid' && !!row.payment_method && (
              <span className="status-pill status-pill--active" style={{ fontSize: 'var(--text-xs)' }}>
                {methodLabels[String(row.payment_method)] || String(row.payment_method)}
              </span>
            )}
          </div>
        );
      }
    }
  ];

  // Filters for DataTable
  const filters: Filter[] = [
    { key: 'status', label: 'Status', options: statusOptions },
    { key: 'territory_name', label: 'Territory', options: territoryOptions, matchFn: (row: Record<string, unknown>, value: string) => (row.territory_name as string) === value }
  ];

  return (
    <div>
      {/* Loading Overlay */}
      {operationMessage && (
        <div className="loading-overlay">
          <div className="spinner" />
          <div className="loading-overlay-message">{operationMessage}</div>
        </div>
      )}

      {/* Lazy modals + invoice editor. Each is conditionally rendered, so
          Suspense kicks in only when one fires. fallback=null because the
          chunks are prefetched on mount (useEffect below) and load in
          milliseconds; a visible spinner here would just flash. */}
      <Suspense fallback={null}>
      {/* View Modal */}
      {viewingLog && (
        <ViewServiceLogModal
          log={viewingLog}
          onClose={() => setViewingLog(null)}
          onEdit={handleEditLog}
          onApprove={handleCreateInvoice}
          onSendInvoice={() => handleSendInvoice(viewingLog.invoice_id!, viewingLog.additional_cc_emails)}
          onViewInvoice={() => { setViewingLog(null); setViewingInvoiceId(viewingLog.invoice_id!); }}
          onWhatsApp={() => handleWhatsApp(viewingLog)}
          onFollowUp={() => handleFollowUp(viewingLog)}
          onUnlinkDraft={() => handleUnlinkDraft(viewingLog)}
          sendingFollowUp={false}
          canEdit={canEdit}
          canApprove={canApprove}
          canSend={canSend}
          canWhatsApp={canWhatsApp}
          canUnlinkDraft={canUnlinkDraft}
          formatCurrency={formatCurrency}
          formatDate={formatDateLocal}
          supabase={supabase}
          countryConfig={countryConfig as never}
          taxRate={taxRate}
        />
      )}

      {/* Send Invoice Modal */}
      {showSendModal && sendingInvoice && (
        <SendInvoiceModal
          invoice={sendingInvoice}
          onClose={() => {
            setShowSendModal(false);
            setSendingInvoice(null);
          }}
          onSuccess={handleSendSuccess}
        />
      )}

      {/* Approve Service Log Modal */}
      {approvingLog && (
        <ApproveServiceLogModal
          log={{
            id: approvingLog.id,
            customer_name: approvingLog.customer_name,
            customer_email: approvingLog.customer_email,
            total_amount: approvingLog.total_amount,
            service_date: approvingLog.service_date,
          }}
          onClose={() => setApprovingLog(null)}
          onConfirm={(overrides) => executeCreateInvoice(approvingLog, overrides)}
        />
      )}

      {/* Send Invoice / Send Reminder Modal */}
      {whatsAppLog && (
        <WhatsAppModal
          log={whatsAppLog}
          onClose={() => setWhatsAppLog(null)}
          onSent={() => setSendHistoryTick(t => t + 1)}
          onResendInvoice={() => {
            const logToResend = whatsAppLog;
            setWhatsAppLog(null);
            if (logToResend.invoice_id) {
              handleSendInvoice(logToResend.invoice_id, logToResend.additional_cc_emails);
            }
          }}
          onEmailReminder={() => {
            const logToRemind = whatsAppLog;
            setWhatsAppLog(null);
            handleFollowUp(logToRemind);
          }}
        />
      )}

      {followUpLog && followUpLog.invoice_id && (
        <FollowUpModal
          invoiceId={followUpLog.invoice_id}
          customerEmail={followUpLog.customer_email || ''}
          invoiceNumber={followUpLog.invoice_number || ''}
          customerName={followUpLog.customer_name || ''}
          totalAmount={followUpLog.total_amount}
          onClose={() => setFollowUpLog(null)}
          onSent={() => { setFollowUpLog(null); showToast('Payment reminder sent', 'success'); loadData(showAllPaid); }}
        />
      )}

      {/* Invoice Detail Modal */}
      {viewingInvoiceId && (
        <InvoiceDetailModal
          invoiceId={viewingInvoiceId}
          mode="view"
          onClose={() => setViewingInvoiceId(null)}
          onRefresh={() => loadData(showAllPaid)}
        />
      )}

      {/* Invoice Manager - Full screen overlay when editing draft/sent invoices */}
      {editingInvoiceNumber && (
        <div className="sl-fullscreen-overlay">
          <div className="sl-fullscreen-inner">
            <div className="mb-sm">
              <button
                className="btn btn-secondary mb-md"
                onClick={handleCloseInvoiceManager}
              >
                &larr; Back to Service Logs
              </button>
            </div>
            <InvoiceManager initialInvoiceNumber={editingInvoiceNumber} />
          </div>
        </div>
      )}
      </Suspense>

      {isMobile ? (
        /* MOBILE VIEW */
        <div className="sl-mobile-wrap">
          <div className="page-tabs mb-md">
            <button className={`page-tab ${mobileViewMode === 'list' ? 'active' : ''}`} onClick={() => setMobileViewMode('list')}>List</button>
            <button className={`page-tab ${mobileViewMode === 'board' ? 'active' : ''}`} onClick={() => setMobileViewMode('board')}>Board</button>
          </div>

          <div className="sl-mobile-btn-row">
            <button className="btn btn-secondary flex-1" onClick={() => loadData(showAllPaid)}>{'\u21BB'} Refresh</button>
            {canCreate && <button className="btn btn-primary flex-1" onClick={handleNewServiceLog}>+ New</button>}
          </div>

          {historyNotice}

          {!loading && !error && serviceLogs.length > 0 && (
            <>
              <div className="list-toolbar">
                <div className="list-toolbar-left">
                  <div className="list-search">
                    <span className="list-search-icon">&#x1F50D;</span>
                    <input
                      type="text"
                      placeholder="Search customer, invoice #, territory..."
                      value={cardSearch}
                      onChange={(e) => setCardSearch(e.target.value)}
                    />
                    {cardSearch && (
                      <button className="list-search-clear" onClick={() => setCardSearch('')}>&times;</button>
                    )}
                  </div>

                  <button
                    className="list-filter-toggle"
                    onClick={() => setMobileFiltersExpanded(v => !v)}
                  >
                    <span>{'⚙'}</span>
                    <span>Filters</span>
                    {(() => { const n = [cardStatusFilter, cardTerritoryFilter].filter(Boolean).length; return n > 0 ? <span className="list-filter-badge">{n}</span> : null; })()}
                    <span className="list-filter-arrow">{mobileFiltersExpanded ? '▲' : '▼'}</span>
                  </button>
                </div>
              </div>

              <div className={`list-filters-mobile ${mobileFiltersExpanded ? 'expanded' : ''}`}>
                <SearchSelect
                  width="inline"
                  options={[
                    { value: '', label: 'Status: All' },
                    ...statusOptions.map(opt => ({ value: opt.value, label: opt.label })),
                  ]}
                  value={cardStatusFilter}
                  onChange={setCardStatusFilter}
                />
                <SearchSelect
                  width="inline"
                  options={[
                    { value: '', label: 'Territory: All' },
                    ...territoryOptions.map(opt => ({ value: opt.value, label: opt.label })),
                  ]}
                  value={cardTerritoryFilter}
                  onChange={setCardTerritoryFilter}
                />
              </div>
            </>
          )}

          {loading ? (
            <div className="placeholder-page"><div className="spinner" /></div>
          ) : error ? (
            <ErrorDisplay error={error} onRetry={() => loadData(showAllPaid)} />
          ) : serviceLogs.length === 0 ? (
            <div className="empty-state"><p>No service logs</p></div>
          ) : mobileViewMode === 'board' ? (
            <BoardView
              logsByStatus={logsByStatus}
              columnSorts={columnSorts}
              onColumnSortChange={setColumnSort}
              onView={handleOpenViewModal}
              onEdit={handleEditLog}
              onCreateInvoice={handleCreateInvoice}
              onSendInvoice={handleSendInvoice}
              onWhatsApp={handleWhatsApp}
              onUnlinkDraft={handleUnlinkDraft}
              canEdit={canEdit}
              canApprove={canApprove}
              canSend={canSend}
              canWhatsApp={canWhatsApp}
              canUnlinkDraft={canUnlinkDraft}
              onFollowUp={handleFollowUp}
              sendingFollowUpFor={null}
              creatingInvoiceFor={creatingInvoiceFor}
              formatCurrency={formatCurrency}
              formatDate={formatDateLocal}
              sendHistory={canSend ? sendHistory : undefined}
            />
          ) : mobileFilteredLogs.length === 0 ? (
            <div className="empty-state"><p>No service logs match your search</p></div>
          ) : (
            <div className="flex-column gap-sm">
              {mobileFilteredLogs.map(log => {
                const pillMap: Record<string, string> = { pending: 'pending', draft: 'draft', sent: 'sent', overdue: 'overdue', paid: 'paid', void: 'voided' };
                const labelMap: Record<string, string> = { pending: 'Pending', draft: 'Draft', sent: 'Sent', overdue: 'Overdue', paid: 'Paid', void: 'Void' };
                const isPending = log.status === 'pending';
                const isDraft = log.status === 'draft';
                const showApprove = canApprove && isPending;
                const showSend = canSend && isDraft;

                return (
                  <div key={log.id} className="sl-mobile-card">
                    <div onClick={() => handleOpenViewModal(log)} className="cursor-pointer">
                      <div className="flex-between mb-xs">
                        <strong>
                          {log.customer_name || 'Unknown'}
                          {log.internal_notes && <span title={String(log.internal_notes)} className="chip chip--warning ml-xs">Note</span>}
                          {(() => {
                            const emailKey = log.customer_email?.toLowerCase() ?? '';
                            const bouncing = bouncingEmails.has(emailKey)
                              || (log.customer_id ? bouncingByCustomerId.has(log.customer_id) : false);
                            if (!bouncing) return null;
                            const bounceInfo = log.customer_id ? bouncingByCustomerId.get(log.customer_id) : undefined;
                            const last = bounceInfo?.lastBounceAt;
                            const days = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86_400_000) : null;
                            const when = days === null ? '' : days <= 0 ? ' today' : days === 1 ? ' 1 day ago' : ` ${days} days ago`;
                            return (
                              <span
                                title={`Customer's email last bounced${when}. Sending the invoice won't deliver until the address is fixed.`}
                                aria-label="Email bouncing"
                                style={{ marginLeft: '6px', fontSize: 'var(--text-base)', cursor: 'help' }}
                              >
                                ⚠️
                              </span>
                            );
                          })()}
                        </strong>
                        <strong className="text-primary">{formatCurrency(log.total_amount || 0)}</strong>
                      </div>
                      <div className="sl-sub-text mb-xs">
                        {formatDateLocal(log.service_date)}{log.territory_name && ` \u00b7 ${log.territory_name}`}{log.operator_name && ` \u00b7 ${log.operator_name}`}
                      </div>
                      <span className={`status-pill status-pill--${pillMap[log.status] || 'draft'}`}>
                        <span className="status-pill-dot" style={{ background: 'currentColor' }} />
                        {labelMap[log.status] || log.status}
                      </span>
                    </div>
                    {(showApprove || showSend) && (
                      <div className="sl-mobile-card-actions">
                        {showApprove && (
                          <button
                            className="btn btn-primary flex-1 sl-touch-btn"
                            onClick={(e) => { e.stopPropagation(); handleCreateInvoice(log); }}
                            disabled={creatingInvoiceFor === log.id}
                          >
                            {creatingInvoiceFor === log.id ? 'Creating...' : 'Approve'}
                          </button>
                        )}
                        {showSend && (
                          <button
                            className="btn btn-primary flex-1 sl-touch-btn"
                            onClick={(e) => { e.stopPropagation(); handleSendInvoice(log.invoice_id!, log.additional_cc_emails); }}
                          >
                            Send
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* DESKTOP VIEW */
        <>
          {viewMode === 'card' && historyNotice}
          <div className="page-tabs">
            <button className={`page-tab ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>List View</button>
            <button className={`page-tab ${viewMode === 'card' ? 'active' : ''}`} onClick={() => setViewMode('card')}>Card View</button>
          </div>
          {viewMode === 'list' ? (
            <DataTable
              columns={columns}
              data={serviceLogs}
              loading={loading}
              searchable={true}
              searchPlaceholder="Search customer, invoice #, split code..."
              searchKeys={['customer_name', 'customer_email', 'invoice_number', 'territory_name', 'operator_name', 'territory_name', 'booking_code']}
              filters={filters}
              defaultFilterValues={{}}
              defaultSort={{ key: 'service_date', direction: 'desc' }}
              pageSize={50}
              // Only head office gets the capped list, so only head office is
              // told about it. An operator already holds all of their own.
              partial={isWideScope && !showAllPaid ? {
                total: totalCount,
                loading,
                onLoadAll: () => setShowAllPaid(true),
                noun: 'service logs',
              } : undefined}
              headerActions={{
                primary: canCreate ? { label: '+ Log', onClick: handleNewServiceLog } : undefined,
                onSync: { label: 'Refresh List', onClick: () => loadData(showAllPaid), tooltip: 'Reloads data from local database (does not sync from Zoho)' },
                overflow: [
                  { label: 'Export CSV', onClick: handleExportCSV },
                ],
              } as HeaderActions}
              actions={(row: Record<string, unknown>) => {
                const isPending = row.status === 'pending';
                const isDraft = row.status === 'draft';
                const isSent = row.status === 'sent';

                const showEdit = canEdit && (isPending || isDraft || isSent || row.status === 'overdue' || row.status === 'paid' || row.status === 'partial' || row.status === 'completed' || row.status === 'viewed');
                const showDeleteDraft = canUnlinkDraft && isDraft && row.invoice_id;
                const showApprove = canApprove && isPending;
                const showSend = canSend && isDraft;
                const typedRow = row as unknown as ServiceLogRow;

                return (
                  <>
                    <span className="action-icon" onClick={(e) => { e.stopPropagation(); handleOpenViewModal(typedRow); }} title="View">👁️</span>
                    {showDeleteDraft ? (
                      <span className="action-icon" onClick={(e) => { e.stopPropagation(); handleUnlinkDraft(typedRow); }} title="Delete draft invoice">🗑️</span>
                    ) : showEdit ? (
                      <span className="action-icon" onClick={(e) => { e.stopPropagation(); handleEditLog(typedRow); }} title="Edit">✏️</span>
                    ) : null}
                    {row.customer_id && (
                      <span className="action-icon" onClick={(e) => { e.stopPropagation(); navigate(`/bookings?action=create&customer_id=${row.customer_id}`); }} title="Rebook">📅</span>
                    )}
                    {showApprove && (
                      <span className="action-icon" onClick={(e) => { e.stopPropagation(); handleCreateInvoice(typedRow); }} title="Approve - Create invoice">✅</span>
                    )}
                    {showSend && (
                      <span className="action-icon" onClick={(e) => { e.stopPropagation(); handleSendInvoice(row.invoice_id as string, row.additional_cc_emails as string); }} title="Send invoice">📤</span>
                    )}
                  </>
                );
              }}
              onRowClick={(row: Record<string, unknown>) => handleOpenViewModal(row as unknown as ServiceLogRow)}
              emptyMessage="No service logs match your filters"
            />
          ) : (
            <>
              <div className="list-toolbar">
                <div className="list-toolbar-left">
                  <div className="list-search">
                    <span className="list-search-icon">&#x1F50D;</span>
                    <input
                      type="text"
                      placeholder="Search customer, invoice #, territory..."
                      value={cardSearch}
                      onChange={(e) => setCardSearch(e.target.value)}
                    />
                    {cardSearch && (
                      <button className="list-search-clear" onClick={() => setCardSearch('')}>&times;</button>
                    )}
                  </div>

                  <SearchSelect
                    width="inline"
                    options={[
                      { value: '', label: 'Territory: All' },
                      ...territoryOptions.map(opt => ({ value: opt.value, label: opt.label })),
                    ]}
                    value={cardTerritoryFilter}
                    onChange={setCardTerritoryFilter}
                  />
                </div>

                <div className="list-toolbar-right">
                  <ToolbarActions
                    primary={canCreate ? { label: '+ Log', onClick: handleNewServiceLog } : undefined}
                    onSync={() => loadData(showAllPaid)}
                  />
                </div>
              </div>

              <BoardView
                logsByStatus={logsByStatus}
                columnSorts={columnSorts}
                onColumnSortChange={setColumnSort}
                onView={handleOpenViewModal}
                onEdit={handleEditLog}
                onCreateInvoice={handleCreateInvoice}
                onSendInvoice={handleSendInvoice}
                onWhatsApp={handleWhatsApp}
                onFollowUp={handleFollowUp}
                onUnlinkDraft={handleUnlinkDraft}
                sendingFollowUpFor={null}
                canEdit={canEdit}
                canApprove={canApprove}
                canSend={canSend}
                canWhatsApp={canWhatsApp}
                canUnlinkDraft={canUnlinkDraft}
                creatingInvoiceFor={creatingInvoiceFor}
                formatCurrency={formatCurrency}
                formatDate={formatDateLocal}
                bouncingByCustomerId={bouncingByCustomerId}
                bouncingEmails={bouncingEmails}
                sendHistory={canSend ? sendHistory : undefined}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// Follow-Up Modal — payment reminder with editable recipient
// ============================================================
function FollowUpModal({
  invoiceId, customerEmail, invoiceNumber, customerName, totalAmount, onClose, onSent,
}: {
  invoiceId: string;
  customerEmail: string;
  invoiceNumber: string;
  customerName: string;
  totalAmount?: number;
  onClose: () => void;
  onSent: () => void;
}) {
  const { formatCurrency } = useConfig();
  const [toEmail, setToEmail] = useState(customerEmail);
  const [ccEmails, setCcEmails] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!toEmail.trim()) { setError('Recipient email is required'); return; }

    setSending(true);
    setError(null);

    const ccList = ccEmails.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-manage?operation=send_follow_up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          invoice_id: invoiceId,
          to_email: toEmail.trim(),
          cc_emails: ccList.length > 0 ? ccList : undefined,
          message: customMessage.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to send reminder');
        setSending(false);
        return;
      }

      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reminder');
      setSending(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Payment Reminder</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="highlight-box highlight-box--warning mb-lg">
              {invoiceNumber ? `Invoice ${invoiceNumber}` : customerName}
              {totalAmount != null && <> — <strong>{formatCurrency(totalAmount)}</strong> due</>}
            </div>

            <div className="form-group">
              <label className="form-label">To <span className="form-required">*</span></label>
              <input
                type="email"
                className="form-input"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="recipient@example.com"
                required
              />
              {toEmail !== customerEmail && customerEmail && (
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '4px' }}>
                  Customer email: {customerEmail}
                  <button
                    type="button"
                    onClick={() => setToEmail(customerEmail)}
                    style={{ marginLeft: '8px', fontSize: 'var(--text-sm)', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">CC</label>
              <input
                type="text"
                className="form-input"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="bookkeeper@example.com, spouse@example.com"
              />
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', marginTop: '4px' }}>
                Separate multiple addresses with commas
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Custom message (optional)</label>
              <textarea
                className="form-input"
                rows={3}
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal note to the reminder..."
              />
            </div>

            {error && (
              <div className="highlight-box highlight-box--danger mt-md">
                {error}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-warning" disabled={sending}>
              {sending ? 'Sending...' : 'Send Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServiceLogs;
